import React, { FC } from 'react';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue, useWatch } from 'react-hook-form';

import { CurrencyInput } from '@eika/currency-input';
import { ButtonToggles } from '@eika/radio-button';

import { ValgtWeb } from '../../generated-types';
import { useTranslation } from '../../translations';
import Arbeidssituasjon from './arbeidssituasjon';
import { InntektFormData } from './inntekt';

interface Props {
    index: number;
    register: UseFormRegister<InntektFormData>;
    control: Control<InntektFormData>;
    setValue: UseFormSetValue<InntektFormData>;
    errors: FieldErrors<InntektFormData>;
}

const InntektSbl: FC<Props> = ({ index, register, control, setValue, errors }) => {
    const { inntekt, validationMessage, button } = useTranslation();
    const value = useWatch({ name: 'inntekt', control });

    const onChangeHarSblInntekt = (value: ValgtWeb) => {
        if (value === ValgtWeb.NEI) {
            setValue(`inntekt.${index}.arbeidssituasjon`, undefined, {
                shouldValidate: false,
            });
        }
        setValue(`inntekt.${index}.harSblInntekt` as const, value);
    };

    return (
        <>
            <span className="title">{value?.[index].arbeidsgiver}</span>
            <Controller
                name={`inntekt.${index}.harSblInntekt` as const}
                control={control}
                render={({ field: { value } }) => (
                    <ButtonToggles
                        label={inntekt.toggelsErNaavaerendeInntekt}
                        data-track-id="kreditt-boliglaan-inntekt-valg-naavaerende-inntektskilde-sbl"
                        data-track-linkname={value}
                        onChange={(value) => onChangeHarSblInntekt(value as ValgtWeb)}
                        selectedOption={value}
                        options={[
                            { label: button.ja, value: ValgtWeb.JA },
                            { label: button.nei, value: ValgtWeb.NEI },
                        ]}
                        errorMessage={
                            errors.harInntekt?.type === 'ikkeValgtHarInntekt'
                                ? `${validationMessage.requiredChoose}`
                                : ''
                        }
                    />
                )}
            />
            {value?.[index].harSblInntekt === ValgtWeb.JA && (
                <>
                    <Arbeidssituasjon
                        index={index}
                        register={register}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                    />
                    <Controller
                        name={`inntekt.${index}.aarsinntektSbl` as const}
                        control={control}
                        rules={{
                            required: `${validationMessage.requiredValue}`,
                        }}
                        render={({ field: { onChange } }) => (
                            <CurrencyInput
                                name={`inntekt.${index}.aarsinntekt` as const}
                                className="max-width-200"
                                label={inntekt.labelInntekt}
                                placeholder=""
                                type="tel"
                                value={value?.[index]?.aarsinntektSbl || 0}
                                error={errors.inntekt?.[index]?.aarsinntektSbl?.message}
                                onValueChange={(v) => {
                                    const newValue = v.value.startsWith('0') ? v.value.slice(1) : v.value;
                                    onChange(newValue);
                                }}
                            />
                        )}
                    />
                </>
            )}
        </>
    );
};

export default InntektSbl;
